import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { EditExtractionRepository } from '@service/associate-extraction/edit/edit-extraction.repository';
import {
  ExtractionForEdit,
  UpdateExtractionData
} from '@model/associate-extraction/edit/edit-extraction.model';

interface ExtractionForEditResponse {
  extractionId: string;
  specimenId: string;
  specimenCode: string;
  lgCode: string | null;
  patientName: string;
  patientIdNumber: string;
  wellLocation: string;
  concentration: number;
  ratio260_280: number;
  ratio260_230: number;
  extractionMethodId: string;
  extractionDate: string;
  responsibleName: string;
  status: string;
}

interface UpdateExtractionRequest {
  extractionId: string;
  wellLocation?: string;
  concentration?: number;
  ratio260_280?: number;
  ratio260_230?: number;
}

interface UpdateExtractionResponse {
  extractionId: string;
  specimenId: string;
  lgCode: string | null;
  concentration: number;
  ratio260_280: number;
  ratio260_230: number;
  extractionMethodId: string;
  wellLocation: string;
  status: string;
  manualEntry: boolean;
  responsibleName: string;
  extractionDate: string;
  createdAt: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class HttpEditExtractionRepository extends EditExtractionRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/specimens/extraction/edit`;

  override searchExtraction(searchCode: string): Observable<ExtractionForEdit> {
    return this.http
      .get<ExtractionForEditResponse>(`${this.BASE_URL}/search/${searchCode}`)
      .pipe(map((response) => this.mapToExtractionForEdit(response)));
  }

  override updateExtraction(data: UpdateExtractionData): Observable<void> {
    const request: UpdateExtractionRequest = {
      extractionId: data.extractionId,
      ...(data.wellLocation !== undefined && {
        wellLocation: data.wellLocation
      }),
      ...(data.concentration !== undefined && {
        concentration: data.concentration
      }),
      ...(data.ratio260_280 !== undefined && {
        ratio260_280: data.ratio260_280
      }),
      ...(data.ratio260_230 !== undefined && {
        ratio260_230: data.ratio260_230
      })
    };

    return this.http
      .patch<UpdateExtractionResponse>(this.BASE_URL, request)
      .pipe(map(() => void 0));
  }

  private mapToExtractionForEdit(
    response: ExtractionForEditResponse
  ): ExtractionForEdit {
    return {
      extractionId: response.extractionId,
      specimenId: response.specimenId,
      specimenCode: response.specimenCode,
      lgCode: response.lgCode,
      patientName: response.patientName,
      patientIdNumber: response.patientIdNumber,
      wellLocation: response.wellLocation,
      concentration: response.concentration,
      ratio260_280: response.ratio260_280,
      ratio260_230: response.ratio260_230,
      extractionMethodId: response.extractionMethodId,
      extractionDate: response.extractionDate,
      responsibleName: response.responsibleName,
      status: response.status
    };
  }
}
